import { useEffect, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { RESERVED_CASE_SEGMENTS } from './workspaces'

const LAST_CASE_KEY = 'spectre:lastCaseId'

function readPersisted(): string | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    return window.localStorage.getItem(LAST_CASE_KEY) ?? undefined
  } catch {
    return undefined
  }
}

function writePersisted(value: string | undefined): void {
  if (typeof window === 'undefined') return
  try {
    if (value) window.localStorage.setItem(LAST_CASE_KEY, value)
    else window.localStorage.removeItem(LAST_CASE_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Tracks the "currently selected" case across navigation.
 *
 * - When the URL is under `/cases/:caseId/*`, that caseId becomes the effective one
 *   and is persisted to localStorage.
 * - When the URL is NOT under a case (e.g. the user visits Settings or Data), the
 *   previously-persisted caseId is returned so the sidebar picker and workspace
 *   links can still point at the user's last-active case.
 */
export function useLastCaseId(): {
  /** The caseId from the current URL, if any. */
  urlCaseId: string | undefined
  /** URL caseId if present, else the most-recently-visited one. */
  effectiveCaseId: string | undefined
} {
  const location = useLocation()
  const match = matchPath({ path: '/cases/:caseId/*', end: false }, location.pathname)
  const rawCaseId = match?.params.caseId
  const urlCaseId = rawCaseId && !RESERVED_CASE_SEGMENTS.has(rawCaseId) ? rawCaseId : undefined

  const [persisted, setPersisted] = useState<string | undefined>(() => readPersisted())

  useEffect(() => {
    if (urlCaseId && urlCaseId !== persisted) {
      writePersisted(urlCaseId)
      setPersisted(urlCaseId)
    }
  }, [urlCaseId, persisted])

  return { urlCaseId, effectiveCaseId: urlCaseId ?? persisted }
}
